import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, createVNode as _createVNode, normalizeClass as _normalizeClass, withCtx as _withCtx, resolveComponent as _resolveComponent, createBlock as _createBlock, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "c-document-library__inner" }
const _hoisted_2 = { class: "c-document-library__filters-header" }
const _hoisted_3 = { key: 0 }
const _hoisted_4 = ["aria-expanded"]
const _hoisted_5 = {
  key: 0,
  class: "c-document-library__filters-title c-document-library__filters-title--desktop"
}
const _hoisted_6 = { class: "c-document-library__filter-group" }
const _hoisted_7 = {
  key: 0,
  class: "c-document-library__filter-heading"
}
const _hoisted_8 = { class: "c-document-library__filter-group" }
const _hoisted_9 = { class: "c-document-library__filter-heading" }
const _hoisted_10 = { class: "c-document-library__toolbar" }
const _hoisted_11 = {
  key: 0,
  class: "c-document-library__list"
}
const _hoisted_12 = ["href"]
const _hoisted_13 = ["src", "alt"]
const _hoisted_14 = {
  key: 1,
  class: "c-document-library__cover-type"
}
const _hoisted_15 = { class: "c-document-library__meta" }
const _hoisted_16 = { class: "c-document-library__headline" }
const _hoisted_17 = {
  key: 0,
  class: "c-document-library__badge"
}
const _hoisted_18 = { class: "c-document-library__item-title" }
const _hoisted_19 = ["href"]
const _hoisted_20 = { class: "c-document-library__description" }
const _hoisted_21 = { class: "c-document-library__file-info" }
const _hoisted_22 = ["href"]
const _hoisted_23 = {
  key: 1,
  class: "c-document-library__empty"
}
const _hoisted_24 = {
  key: 2,
  class: "c-document-library__empty"
}
const _hoisted_25 = {
  key: 3,
  class: "c-document-library__loading",
  role: "status",
  "aria-live": "polite"
}

import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import FormInput from '@/elements/FormInput.vue';
import Checkbox from '@/elements/Checkbox.vue';
import api from '@/api';
import biologicsCover from '@/assets/images/handbooks/biologics.png';
import cellTherapyCover from '@/assets/images/handbooks/cell-therapy.png';
import discoveryCover from '@/assets/images/handbooks/discovery.png';
import geneTherapyCover from '@/assets/images/handbooks/gene-therapy.png';
import mrnaLnpCover from '@/assets/images/handbooks/mrna-lnp.png';
import {
  DocumentLibraryDocument,
  DocumentLibraryFilterOption,
  DocumentLibraryProps,
} from '@/types/components/DocumentLibrary';
import utils from '@/utils';


export default /*@__PURE__*/_defineComponent({
  __name: 'DocumentLibrary',
  props: {
    dataSource: {},
    fields: {},
    rendering: {},
    params: {}
  },
  setup(__props: any) {

const props = __props;
const route = useRoute();

const normalizeSearchKeyword = (value: unknown) => {
  const keyword = Array.isArray(value) ? value[0] : value;
  return typeof keyword === 'string' ? keyword.trim() : '';
};

const rootClass = computed(() => utils.overall.baseComClass(props.fields, 'c-document-library'));
const rootStyle = computed(() => utils.overall?.baseComStyle?.(fields.value) || {});

const searchKeyword = ref(normalizeSearchKeyword(route.query.kw));
const selectedProductLines = ref<string[]>([]);
const selectedResourceTypes = ref<string[]>(['all-resources']);
const currentPage = ref(1);
const totalCount = ref(0);
const isLoading = ref(false);
const documents = ref<DocumentLibraryDocument[]>([]);
const mobileFiltersOpen = ref(false);
let filterReloadTimer: ReturnType<typeof setTimeout> | null = null;
let latestRequestId = 0;

const fields = computed(() => props.fields);

const productLineCovers: Record<string, string> = {
  biologics: biologicsCover,
  'cell-therapy': cellTherapyCover,
  discovery: discoveryCover,
  'gene-therapy': geneTherapyCover,
  'mrna-lnp': mrnaLnpCover,
};

const getFieldValue = (field: any, defaultValue = '') => field?.value ?? defaultValue;

const getRootFieldValue = (fieldName: string, defaultValue = '') => {
  const field = fields.value?.[
    fieldName as keyof NonNullable<DocumentLibraryProps['fields']>
  ] as any;
  return getFieldValue(field, defaultValue);
};

const pageSize = computed(() => {
  const value = (fields.value as any)?.pageSize?.value;
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : 10;
});

const totalPages = computed(() => Math.max(1, Math.ceil(totalCount.value / pageSize.value)));

const normalizeKey = (value?: string) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-');

const stripHtml = (value?: string) => String(value || '').replace(/<[^>]*>/g, '');

const getDictionaryText = (field: any) =>
  field?.fields?.text?.value ||
  field?.value?.text?.value ||
  field?.text?.value ||
  field?.displayName ||
  field?.name ||
  (typeof field?.value === 'string' ? field.value : '') ||
  '';

const getDictionaryValue = (field: any) =>
  field?.fields?.phrase?.value ||
  field?.value?.phrase?.value ||
  field?.phrase?.value ||
  field?.name ||
  (typeof field?.value === 'string' ? field.value : '') ||
  '';

const getFirstSelectedItem = (field: any) => {
  const value = field?.value;
  return Array.isArray(value) ? value[0] : field;
};

const normalizeOption = (item: any): DocumentLibraryFilterOption => ({
  id: String(item?.id ?? item?.fields?.phrase?.value ?? item?.name ?? ''),
  label: String(item?.fields?.text?.value ?? item?.displayName ?? item?.name ?? ''),
  value: normalizeKey(item?.fields?.phrase?.value ?? item?.name ?? ''),
});

const formatFileSize = (size: any) => {
  const numericSize = Number(size);
  if (!Number.isFinite(numericSize) || numericSize <= 0) return '';
  if (numericSize < 1024) return `${numericSize} B`;
  if (numericSize < 1024 * 1024) return `${(numericSize / 1024).toFixed(0)} KB`;
  return `${(numericSize / 1024 / 1024).toFixed(1)} MB`;
};

const getFileExtension = (file: any, fallback = 'PDF') => {
  const src = file?.value?.src || file?.src || '';
  const extension = src.split('?')[0].split('.').pop();
  return extension ? extension.toUpperCase() : fallback;
};

const normalizeDocument = (item: any): DocumentLibraryDocument => {
  const itemFields = item?.fields || {};
  const title = stripHtml(getFieldValue(itemFields.title) || item?.displayName || item?.name || '');
  const file = itemFields.file;
  const productLineValue = normalizeKey(
    getDictionaryValue(getFirstSelectedItem(itemFields.productLine))
  );
  const resourceTypeValue = normalizeKey(getDictionaryValue(itemFields.resourceType));
  const tagText = getFieldValue(itemFields.tag) || getDictionaryText(itemFields.resourceType);
  const fileType = getFileExtension(file, getFieldValue(itemFields.fileType, 'PDF'));
  const fileSize = getFieldValue(itemFields.fileSize) || formatFileSize(file?.value?.size);
  const fileHref = file?.value?.src || itemFields.button?.value?.link?.value?.href || '#';

  return {
    id: String(item?.id ?? item?.name ?? title),
    title,
    description: stripHtml(
      getFieldValue(itemFields.description) || getFieldValue(itemFields.pageTitle)
    ),
    productLine: productLineValue,
    resourceType: resourceTypeValue,
    resourceTypeLabel: tagText,
    fileType,
    fileSize,
    isNew: Boolean(itemFields.isNew?.value),
    detailUrl: item?.fields.url?.value?.url || '#',
    coverImage: {
      src: productLineCovers[productLineValue] || '',
      alt: title,
    },
    downloadLink: {
      href: fileHref,
      target: '_blank',
    },
  };
};

const productLines = computed(() => (fields.value?.productLines || []).map(normalizeOption));
const resourceTypes = computed(() => [
  {
    id: 'all-resources',
    label: getRootFieldValue('allResourcesLabel', 'All Resources'),
    value: 'all-resources',
  },
  ...(fields.value?.resourceTypes || []).map(normalizeOption),
]);

const getOptionValue = (item: DocumentLibraryFilterOption) => item.value;
const getOptionLabel = (item: DocumentLibraryFilterOption) => item.label;

const getCurrentLanguage = () => {
  const page = (props as any).page;
  if (page?.itemLanguage) return page.itemLanguage;
  if (typeof document !== 'undefined') return document.documentElement.lang || 'en-US';
  return 'en-US';
};

const normalizeApiData = (res: any, ex: any) => res || ex || {};

const loadDocuments = async () => {
  const requestId = ++latestRequestId;
  isLoading.value = true;
  try {
    const [res, ex] = await api.documents.getDocuments({
      // lang: getCurrentLanguage(),
      keyword: searchKeyword.value.trim(),
      page: currentPage.value,
      size: pageSize.value,
      productLine: selectedProductLines.value.join(','),
      resourceType: selectedResourceTypes.value.includes('all-resources')
        ? ''
        : selectedResourceTypes.value.join(','),
    });
    if (requestId !== latestRequestId) return;

    const data = normalizeApiData(res, ex);
    documents.value = (data?.list || []).map(normalizeDocument);
    totalCount.value = Number(data?.total) || documents.value.length;
  } catch (error) {
    if (requestId !== latestRequestId) return;
    documents.value = [];
    totalCount.value = 0;
  } finally {
    if (requestId === latestRequestId) {
      isLoading.value = false;
    }
  }
};

const clearFilterReloadTimer = () => {
  if (!filterReloadTimer) return;
  clearTimeout(filterReloadTimer);
  filterReloadTimer = null;
};

const reloadFromFirstPage = () => {
  clearFilterReloadTimer();
  if (currentPage.value === 1) {
    loadDocuments();
    return;
  }
  currentPage.value = 1;
};

const debounceReloadFromFirstPage = () => {
  clearFilterReloadTimer();
  latestRequestId += 1;
  filterReloadTimer = setTimeout(() => {
    filterReloadTimer = null;
    reloadFromFirstPage();
  }, 1500);
};

const toggleSelected = (target: typeof selectedProductLines, value: string, checked: boolean) => {
  const next = new Set(target.value);
  if (checked) {
    next.add(value);
  } else {
    next.delete(value);
  }
  target.value = Array.from(next);
};

const onProductLineChange = (item: DocumentLibraryFilterOption, checked: boolean) => {
  toggleSelected(selectedProductLines, getOptionValue(item), checked);
  debounceReloadFromFirstPage();
};

const onResourceTypeChange = (item: DocumentLibraryFilterOption, checked: boolean) => {
  const value = getOptionValue(item);
  if (value === 'all-resources') {
    selectedResourceTypes.value = ['all-resources'];
    debounceReloadFromFirstPage();
    return;
  }
  if (checked) {
    selectedResourceTypes.value = selectedResourceTypes.value.filter(
      (selectedValue) => selectedValue !== 'all-resources'
    );
  }
  toggleSelected(selectedResourceTypes, value, checked);
  if (selectedResourceTypes.value.length === 0) {
    selectedResourceTypes.value = ['all-resources'];
  }
  debounceReloadFromFirstPage();
};

const onSearchChange = (_event: Event, value: string) => {
  searchKeyword.value = value;
  debounceReloadFromFirstPage();
};

const getDetailHref = (document: DocumentLibraryDocument) => document.detailUrl || '#';
const getCoverSrc = (document: DocumentLibraryDocument) => document.coverImage?.src || '';
const getCoverAlt = (document: DocumentLibraryDocument) =>
  document.coverImage?.alt || document.title;
const getCoverResourceType = (document: DocumentLibraryDocument) =>
  String(document.resourceTypeLabel || document.resourceType || '')
    .trim()
    .toUpperCase();
const getFileInfo = (document: DocumentLibraryDocument) =>
  [document.fileType || 'PDF', document.fileSize].filter(Boolean).join(' - ');

const toggleMobileFilters = () => {
  mobileFiltersOpen.value = !mobileFiltersOpen.value;
};

watch(currentPage, loadDocuments);
watch(pageSize, reloadFromFirstPage);
watch(
  () => route.query.kw,
  (value) => {
    const keyword = normalizeSearchKeyword(value);
    if (keyword === searchKeyword.value) return;
    searchKeyword.value = keyword;
    reloadFromFirstPage();
  }
);

onMounted(loadDocuments);
onBeforeUnmount(clearFilterReloadTimer);

return (_ctx: any,_cache: any) => {
  const _component_Pagination = _resolveComponent("Pagination")!
  const _component_ContentGrid = _resolveComponent("ContentGrid")!

  return (fields.value)
    ? (_openBlock(), _createElementBlock("section", {
        key: 0,
        class: _normalizeClass(rootClass.value),
        style: _normalizeStyle(rootStyle.value)
      }, [
        _createVNode(_component_ContentGrid, {
          "desktop-default-grid": [2, 20],
          "tablet-default-grid": [1, 14],
          "mobile-default-grid": [1, 14]
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_1, [
              _createElementVNode("aside", {
                class: _normalizeClass(["c-document-library__filters", { 'is-open': mobileFiltersOpen.value }]),
                "aria-label": "Document filters"
              }, [
                _createElementVNode("div", _hoisted_2, [
                  (getRootFieldValue('filtersTitle', 'Filters'))
                    ? (_openBlock(), _createElementBlock("h2", _hoisted_3, _toDisplayString(getRootFieldValue('filtersTitle', 'Filters')), 1))
                    : _createCommentVNode("", true),
                  _createElementVNode("button", {
                    class: "c-document-library__mobile-filter-toggle",
                    type: "button",
                    "aria-expanded": mobileFiltersOpen.value,
                    onClick: toggleMobileFilters
                  }, [
                    _createElementVNode("span", null, _toDisplayString(getRootFieldValue('productLineTitle', 'Product Line')), 1),
                    _cache[1] || (_cache[1] = _createElementVNode("span", {
                      class: "c-document-library__mobile-filter-icon",
                      "aria-hidden": "true"
                    }, null, -1))
                  ], 8, _hoisted_4)
                ]),
                (getRootFieldValue('filtersTitle', 'Filters'))
                  ? (_openBlock(), _createElementBlock("h2", _hoisted_5, _toDisplayString(getRootFieldValue('filtersTitle', 'Filters')), 1))
                  : _createCommentVNode("", true),
                _createElementVNode("div", _hoisted_6, [
                  (getRootFieldValue('productLineTitle', 'Product Line'))
                    ? (_openBlock(), _createElementBlock("h3", _hoisted_7, _toDisplayString(getRootFieldValue('productLineTitle', 'Product Line')), 1))
                    : _createCommentVNode("", true),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(productLines.value, (item) => {
                    return (_openBlock(), _createElementBlock("label", {
                      key: item.id || getOptionValue(item),
                      class: "c-document-library__filter-option"
                    }, [
                      _createVNode(Checkbox, {
                        value: selectedProductLines.value.includes(getOptionValue(item)),
                        onChange: (_event, checked) => onProductLineChange(item, checked)
                      }, null, 8, ["value", "onChange"]),
                      _createElementVNode("span", null, _toDisplayString(getOptionLabel(item)), 1)
                    ]))
                  }), 128))
                ]),
                _createElementVNode("div", _hoisted_8, [
                  _createElementVNode("h3", _hoisted_9, _toDisplayString(getRootFieldValue('resourceTypeTitle', 'Resource Type')), 1),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(resourceTypes.value, (item) => {
                    return (_openBlock(), _createElementBlock("label", {
                      key: item.id || getOptionValue(item),
                      class: "c-document-library__filter-option"
                    }, [
                      _createVNode(Checkbox, {
                        value: selectedResourceTypes.value.includes(getOptionValue(item)),
                        onChange: (_event, checked) => onResourceTypeChange(item, checked)
                      }, null, 8, ["value", "onChange"]),
                      _createElementVNode("span", null, _toDisplayString(getOptionLabel(item)), 1)
                    ]))
                  }), 128))
                ])
              ], 2),
              _createElementVNode("div", {
                class: _normalizeClass(["c-document-library__content", { 'is-loading': isLoading.value }])
              }, [
                _createElementVNode("div", _hoisted_10, [
                  _cache[3] || (_cache[3] = _createElementVNode("h2", { class: "c-document-library__title" }, "Document Library", -1)),
                  _createVNode(FormInput, {
                    class: "c-document-library__search",
                    value: searchKeyword.value,
                    placeholder: getRootFieldValue('searchPlaceholder', 'Search'),
                    "data-name": "document-library-search",
                    onChange: onSearchChange
                  }, {
                    icon: _withCtx(() => [...(_cache[2] || (_cache[2] = [
                      _createElementVNode("svg", {
                        class: "c-document-library__search-icon",
                        viewBox: "0 0 16 16",
                        "aria-hidden": "true"
                      }, [
                        _createElementVNode("path", {
                          d: "M7.2 12.4A5.2 5.2 0 1 1 7.2 2a5.2 5.2 0 0 1 0 10.4Zm0-1.2a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm3.7.55.85-.85 2.75 2.75-.85.85-2.75-2.75Z",
                          fill: "currentColor"
                        })
                      ], -1)
                    ]))]),
                    _: 1
                  }, 8, ["value", "placeholder"])
                ]),
                (documents.value.length)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(documents.value, (document) => {
                        return (_openBlock(), _createElementBlock("article", {
                          key: document.id || document.title,
                          class: "c-document-library__item"
                        }, [
                          _createElementVNode("a", {
                            class: "c-document-library__cover",
                            href: getDetailHref(document),
                            target: "_block"
                          }, [
                            (getCoverSrc(document))
                              ? (_openBlock(), _createElementBlock("img", {
                                  key: 0,
                                  src: getCoverSrc(document),
                                  alt: getCoverAlt(document)
                                }, null, 8, _hoisted_13))
                              : _createCommentVNode("", true),
                            (getCoverResourceType(document))
                              ? (_openBlock(), _createElementBlock("span", _hoisted_14, _toDisplayString(getCoverResourceType(document)), 1))
                              : _createCommentVNode("", true)
                          ], 8, _hoisted_12),
                          _createElementVNode("div", _hoisted_15, [
                            _createElementVNode("div", _hoisted_16, [
                              (document.isNew)
                                ? (_openBlock(), _createElementBlock("span", _hoisted_17, "New"))
                                : _createCommentVNode("", true),
                              _createElementVNode("h3", _hoisted_18, [
                                _createElementVNode("a", {
                                  href: getDetailHref(document),
                                  target: "_block"
                                }, _toDisplayString(document.title), 9, _hoisted_19)
                              ])
                            ]),
                            _createElementVNode("p", _hoisted_20, _toDisplayString(document.description), 1),
                            _createElementVNode("p", _hoisted_21, _toDisplayString(getFileInfo(document)), 1)
                          ]),
                          _createElementVNode("a", {
                            class: "c-document-library__download",
                            href: getDetailHref(document),
                            target: "_block"
                          }, " Learn More ", 8, _hoisted_22)
                        ]))
                      }), 128))
                    ]))
                  : (isLoading.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_23, "Loading..."))
                    : (_openBlock(), _createElementBlock("div", _hoisted_24, _toDisplayString(getRootFieldValue('emptyMessage', 'No documents match your filters.')), 1)),
                (isLoading.value && documents.value.length)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_25, [...(_cache[4] || (_cache[4] = [
                      _createElementVNode("span", {
                        class: "c-document-library__loading-spinner",
                        "aria-hidden": "true"
                      }, null, -1),
                      _createElementVNode("span", null, "Loading...", -1)
                    ]))]))
                  : _createCommentVNode("", true),
                (totalPages.value > 1)
                  ? (_openBlock(), _createBlock(_component_Pagination, {
                      key: 4,
                      currentPage: currentPage.value,
                      "onUpdate:currentPage": _cache[0] || (_cache[0] = ($event: any) => ((currentPage).value = $event)),
                      totalPages: totalPages.value
                    }, null, 8, ["currentPage", "totalPages"]))
                  : _createCommentVNode("", true)
              ], 2)
            ])
          ]),
          _: 1
        })
      ], 6))
    : _createCommentVNode("", true)
}
}

})